import { useState, useEffect } from "react";
import API, { formatError } from "@/api";
import { toast } from "sonner";
import { Plus, UserCheck, UserX, Search, Pencil, Trash2, Sparkles, Key, Save, Eye, EyeOff, Database, HardDrive, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { displayRole, ROLE_LABELS } from "@/lib/roles";

const ROLE_OPTIONS = [
    { value: "member", label: "Membru" },
    { value: "admin", label: "Manager Departament" },
    { value: "super_admin", label: "Super Admin" },
];

function formatBytes(bytes) {
    if (!bytes || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function StatCard({ icon, label, value, accent }) {
    return (
        <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1.5">
                <span className={accent}>{icon}</span>
                {label}
            </div>
            <p className={`text-xl font-bold ${accent}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</p>
        </div>
    );
}

export default function AdminPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("users");
    const [showCreate, setShowCreate] = useState(false);
    const [search, setSearch] = useState("");
    const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });
    const [saving, setSaving] = useState(false);

    // Edit user state
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", nickname: "", job_title: "", role: "member", custom_role_label: "" });

    // AI settings state
    const [aiSettings, setAiSettings] = useState({ use_custom_key: false, has_custom_key: false, custom_key_masked: "" });
    const [aiKeyInput, setAiKeyInput] = useState("");
    const [aiUseCustom, setAiUseCustom] = useState(false);
    const [aiShowKey, setAiShowKey] = useState(false);
    const [aiSaving, setAiSaving] = useState(false);

    // DB stats state
    const [dbStats, setDbStats] = useState(null);
    const [dbStatsLoading, setDbStatsLoading] = useState(false);

    useEffect(() => {
        if (!["super_admin", "admin"].includes(user?.role)) {
            navigate("/");
            return;
        }
        fetchData();
    }, [user]);

    useEffect(() => {
        if (activeTab === "ai") fetchAiSettings();
        if (activeTab === "storage") fetchDbStats();
    }, [activeTab]);

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            API.get("/admin/users"),
            API.get("/admin/activity-logs?limit=50"),
        ]).then(([usersRes, logsRes]) => {
            setUsers(usersRes.data);
            setLogs(logsRes.data);
        }).catch(() => toast.error("Eroare la încărcarea datelor"))
            .finally(() => setLoading(false));
    };

    const fetchAiSettings = () => {
        API.get("/admin/settings/ai").then(r => {
            setAiSettings(r.data);
            setAiUseCustom(r.data.use_custom_key);
        }).catch(() => {});
    };

    const fetchDbStats = () => {
        setDbStatsLoading(true);
        API.get("/admin/db-stats").then(r => {
            setDbStats(r.data);
        }).catch(() => toast.error("Eroare la încărcarea statisticilor DB"))
            .finally(() => setDbStatsLoading(false));
    };

    const createUser = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await API.post("/admin/users", form);
            toast.success("Utilizator creat");
            setShowCreate(false);
            setForm({ name: "", email: "", password: "", role: "member" });
            fetchData();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const toggleStatus = async (userId, current) => {
        const newStatus = current === "active" ? "inactive" : "active";
        try {
            await API.patch(`/admin/users/${userId}/status`, { status: newStatus });
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
            toast.success(`Utilizator ${newStatus === "active" ? "activat" : "dezactivat"}`);
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const openEditUser = (u) => {
        setEditUser(u);
        setEditForm({
            name: u.name || "",
            nickname: u.nickname || "",
            job_title: u.job_title || "",
            role: u.role || "member",
            custom_role_label: u.custom_role_label || "",
        });
    };

    const saveEditUser = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...editForm };
            // Don't send role if not changed (avoid self-role-change 400)
            if (payload.role === editUser.role) delete payload.role;
            // Normalize custom label: send even if empty string to allow clearing
            payload.custom_role_label = (payload.custom_role_label || "").trim();
            await API.patch(`/users/${editUser.id}`, payload);
            toast.success("Utilizator actualizat");
            setEditUser(null);
            fetchData();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteUser = async (u) => {
        if (!window.confirm(`Ștergi definitiv utilizatorul "${u.name}" (${u.email})? Această acțiune nu se poate anula.`)) return;
        try {
            await API.delete(`/users/${u.id}`);
            toast.success("Utilizator șters");
            fetchData();
        } catch (err) {
            toast.error(formatError(err));
        }
    };

    const saveAiSettings = async (e) => {
        e.preventDefault();
        setAiSaving(true);
        try {
            const payload = { use_custom_key: aiUseCustom };
            if (aiKeyInput.trim()) payload.anthropic_api_key = aiKeyInput.trim();
            await API.put("/admin/settings/ai", payload);
            toast.success("Setări AI salvate");
            setAiKeyInput("");
            fetchAiSettings();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setAiSaving(false);
        }
    };

    const filteredUsers = users.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    const ACTION_LABELS = {
        "task.created": "a creat task",
        "task.updated": "a actualizat task",
        "task.moved": "a mutat task",
        "board.created": "a creat board",
        "team.created": "a creat echipă",
        "admin.user_created": "a creat utilizator",
        "admin.user_status_active": "a activat utilizator",
        "admin.user_status_inactive": "a dezactivat utilizator",
        "user.updated": "a actualizat profil utilizator",
        "user.deleted": "a șters utilizator",
        "settings.ai_updated": "a actualizat setările AI",
    };

    const isSuper = user?.role === "super_admin";

    return (
        <div className="p-6 max-w-6xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Manager Departament</h1>
                {activeTab === "users" && (
                    <button onClick={() => setShowCreate(true)} data-testid="create-user-btn"
                        className="flex items-center gap-1.5 text-xs bg-[#002FA7] text-white px-3 py-2 rounded-md hover:bg-[#002482] transition-colors">
                        <Plus size={14} /> Utilizator nou
                    </button>
                )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[#E4E4E7] dark:border-[#1E232B] mb-6">
                {[
                    { id: "users", label: `Utilizatori (${users.length})` },
                    { id: "ai", label: "Setări AI" },
                    { id: "storage", label: "Storage" },
                    { id: "logs", label: `Audit Log (${logs.length})` },
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        data-testid={`admin-tab-${tab.id}`}
                        className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors
                            ${activeTab === tab.id ? "border-[#002FA7] text-[#002FA7] dark:text-[#6A91FF]" : "border-transparent text-[#9CA3AF] hover:text-[#4B5563]"}`}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === "users" && (
                loading ? (
                    <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md animate-pulse" />)}</div>
                ) : (
                    <div>
                        <div className="mb-4 relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                            <input data-testid="admin-user-search" value={search} onChange={e => setSearch(e.target.value)}
                                placeholder="Caută utilizator..."
                                className="pl-8 pr-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] w-full max-w-sm" />
                        </div>
                        <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md overflow-hidden" data-testid="users-table">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-[#E4E4E7] dark:border-[#252B33] bg-[#F8F9FA] dark:bg-[#1C222A]">
                                        <th className="text-left px-5 py-3 text-xs font-bold text-[#9CA3AF] uppercase tracking-wide">Utilizator</th>
                                        <th className="text-left px-5 py-3 text-xs font-bold text-[#9CA3AF] uppercase tracking-wide">Rol</th>
                                        <th className="text-left px-5 py-3 text-xs font-bold text-[#9CA3AF] uppercase tracking-wide">Status</th>
                                        <th className="text-left px-5 py-3 text-xs font-bold text-[#9CA3AF] uppercase tracking-wide">Creat</th>
                                        <th className="px-5 py-3 text-right text-xs font-bold text-[#9CA3AF] uppercase tracking-wide">Acțiuni</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#F1F3F5] dark:divide-[#1E232B]">
                                    {filteredUsers.map(u => (
                                        <tr key={u.id} className="hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] transition-colors" data-testid={`user-row-${u.id}`}>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: u.avatar || "#002FA7" }}>
                                                        <span className="text-white text-xs font-bold">{u.name?.[0]?.toUpperCase()}</span>
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-[#111827] dark:text-[#F3F4F6]">{u.name}</p>
                                                        <p className="text-xs text-[#9CA3AF]">{u.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3">
                                                <span className="text-xs bg-blue-50 dark:bg-[#1A2338] text-[#002FA7] dark:text-[#6A91FF] px-2 py-0.5 rounded-sm font-semibold"
                                                    title={u.custom_role_label ? `Rol real: ${ROLE_LABELS[u.role] || u.role}` : ""}>
                                                    {displayRole(u)}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3">
                                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm
                                                    ${u.status === "active" ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400"}`}>
                                                    {u.status === "active" ? "Activ" : "Inactiv"}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-xs text-[#9CA3AF]">
                                                {u.created_at ? new Date(u.created_at).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "2-digit" }) : "-"}
                                            </td>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}
                                                        className="text-xs flex items-center gap-1 px-2 py-1 rounded text-[#002FA7] hover:bg-blue-50 dark:hover:bg-[#1A2338] transition-colors"
                                                        title="Editează">
                                                        <Pencil size={13} />
                                                    </button>
                                                    {u.id !== user?.id && (
                                                        <button onClick={() => toggleStatus(u.id, u.status)} data-testid={`toggle-status-${u.id}`}
                                                            className={`text-xs flex items-center gap-1 px-2 py-1 rounded transition-colors
                                                                ${u.status === "active" ? "text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20" : "text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"}`}
                                                            title={u.status === "active" ? "Dezactivează" : "Activează"}>
                                                            {u.status === "active" ? <UserX size={13} /> : <UserCheck size={13} />}
                                                        </button>
                                                    )}
                                                    {u.id !== user?.id && (isSuper || u.role !== "super_admin") && (
                                                        <button onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`}
                                                            className="text-xs flex items-center gap-1 px-2 py-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                            title="Șterge">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredUsers.length === 0 && (
                                <div className="text-center py-10 text-sm text-[#9CA3AF]">Niciun utilizator găsit</div>
                            )}
                        </div>
                    </div>
                )
            )}

            {activeTab === "ai" && (
                <div className="max-w-2xl" data-testid="admin-ai-settings">
                    <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-6">
                        <div className="flex items-start gap-3 mb-5">
                            <div className="w-10 h-10 rounded-md bg-[#EEF2FF] dark:bg-[#1A2338] flex items-center justify-center flex-shrink-0">
                                <Sparkles size={18} className="text-[#002FA7] dark:text-[#6A91FF]" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-[#111827] dark:text-[#F3F4F6]">Claude Sonnet 4.5 — Sinteze AI</h3>
                                <p className="text-xs text-[#9CA3AF] mt-1">
                                    Sintezele AI din Rapoarte necesită o cheie Anthropic. Adaugă cheia ta de la console.anthropic.com pentru a activa sintezele.
                                </p>
                            </div>
                        </div>

                        <form onSubmit={saveAiSettings} className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-[#F8F9FA] dark:bg-[#1C222A] rounded-md">
                                <div>
                                    <p className="text-sm font-semibold text-[#111827] dark:text-[#F3F4F6]">Folosește cheia mea Anthropic</p>
                                    <p className="text-xs text-[#9CA3AF] mt-0.5">
                                        Activează și introdu cheia ta Anthropic pentru a folosi sintezele AI.
                                    </p>
                                </div>
                                <button type="button"
                                    data-testid="ai-use-custom-toggle"
                                    onClick={() => setAiUseCustom(!aiUseCustom)}
                                    className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${aiUseCustom ? "bg-[#002FA7]" : "bg-[#E4E4E7] dark:bg-[#2A3139]"}`}>
                                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${aiUseCustom ? "translate-x-5" : "translate-x-0.5"}`} />
                                </button>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">
                                    Cheie Anthropic {aiSettings.has_custom_key && <span className="text-[#9CA3AF] normal-case ml-2">(curent: {aiSettings.custom_key_masked})</span>}
                                </label>
                                <div className="relative">
                                    <Key size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                                    <input
                                        type={aiShowKey ? "text" : "password"}
                                        data-testid="ai-key-input"
                                        value={aiKeyInput}
                                        onChange={e => setAiKeyInput(e.target.value)}
                                        placeholder="sk-ant-api03-..."
                                        autoComplete="new-password"
                                        className="w-full pl-9 pr-10 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                                    <button type="button" onClick={() => setAiShowKey(!aiShowKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827]">
                                        {aiShowKey ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                                <p className="text-[10px] text-[#9CA3AF] mt-1">Lasă gol pentru a păstra cheia existentă. Obține o cheie de la console.anthropic.com.</p>
                            </div>

                            <button type="submit" disabled={aiSaving}
                                data-testid="ai-save-btn"
                                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60 transition-colors">
                                <Save size={14} /> {aiSaving ? "Se salvează..." : "Salvează"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {activeTab === "storage" && (
                <div className="max-w-4xl" data-testid="admin-storage-section">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-semibold text-[#4B5563] dark:text-[#C4C9D4]">Utilizare bază de date</h2>
                        <button onClick={fetchDbStats} disabled={dbStatsLoading}
                            data-testid="refresh-db-stats"
                            className="flex items-center gap-1 text-xs text-[#9CA3AF] hover:text-[#002FA7] px-2 py-1 rounded transition-colors">
                            <RefreshCw size={12} className={dbStatsLoading ? "animate-spin" : ""} />
                            Reîmprospătează
                        </button>
                    </div>

                    {dbStatsLoading && !dbStats && (
                        <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-8 text-center text-sm text-[#9CA3AF]">
                            Se încarcă statisticile...
                        </div>
                    )}

                    {dbStats && (
                        <>
                            {/* Storage overview cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                                <StatCard icon={<HardDrive size={14} />} label="Date" value={formatBytes(dbStats.storage.data_size_bytes)} accent="text-[#002FA7] dark:text-[#6A91FF]" />
                                <StatCard icon={<Database size={14} />} label="Index" value={formatBytes(dbStats.storage.index_size_bytes)} accent="text-purple-600 dark:text-purple-400" />
                                <StatCard icon={<Database size={14} />} label="Total" value={formatBytes(dbStats.storage.total_size_bytes)} accent="text-green-600 dark:text-green-400" />
                                <StatCard icon={<Database size={14} />} label="Documente" value={dbStats.storage.documents.toLocaleString("ro-RO")} accent="text-orange-500 dark:text-orange-400" />
                            </div>

                            {/* Business entities overview */}
                            <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5 mb-5" data-testid="db-entities-section">
                                <h3 className="font-semibold text-sm text-[#111827] dark:text-[#F3F4F6] mb-3">Entități business</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                    {[
                                        { label: "Utilizatori", v: `${dbStats.entities.users_active}/${dbStats.entities.users_total} activi` },
                                        { label: "Workspaces", v: dbStats.entities.workspaces_active },
                                        { label: "Echipe", v: dbStats.entities.teams_active },
                                        { label: "Board-uri", v: dbStats.entities.boards_active },
                                        { label: "Task-uri active", v: dbStats.entities.tasks_active },
                                        { label: "Task-uri finalizate", v: dbStats.entities.tasks_completed },
                                        { label: "Mesaje chat", v: dbStats.entities.chat_messages_total },
                                        { label: "Notificări necitite", v: dbStats.entities.notifications_unread },
                                    ].map(e => (
                                        <div key={e.label} className="flex items-center justify-between border-b border-[#F1F3F5] dark:border-[#1E232B] py-1.5">
                                            <span className="text-xs text-[#9CA3AF]">{e.label}</span>
                                            <span className="font-semibold text-[#111827] dark:text-[#F3F4F6]">{e.v}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Per-collection breakdown */}
                            <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md" data-testid="db-collections-table">
                                <div className="px-5 py-3 border-b border-[#E4E4E7] dark:border-[#252B33]">
                                    <h3 className="font-semibold text-sm text-[#111827] dark:text-[#F3F4F6]">Colecții ({dbStats.collections.length})</h3>
                                </div>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-[#F1F3F5] dark:border-[#1E232B]">
                                            <th className="text-left px-5 py-2 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">Colecție</th>
                                            <th className="text-right px-5 py-2 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">Documente</th>
                                            <th className="text-right px-5 py-2 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">Date</th>
                                            <th className="text-right px-5 py-2 text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">Storage</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#F1F3F5] dark:divide-[#1E232B]">
                                        {dbStats.collections.map(c => (
                                            <tr key={c.name} className="hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A]">
                                                <td className="px-5 py-2 font-mono text-xs text-[#111827] dark:text-[#F3F4F6]">{c.name}</td>
                                                <td className="px-5 py-2 text-right text-[#4B5563] dark:text-[#C4C9D4]">{c.documents.toLocaleString("ro-RO")}</td>
                                                <td className="px-5 py-2 text-right text-xs text-[#9CA3AF]">{formatBytes(c.data_size_bytes)}</td>
                                                <td className="px-5 py-2 text-right text-xs text-[#9CA3AF]">{formatBytes(c.storage_size_bytes)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <p className="text-[10px] text-[#9CA3AF] mt-3 italic">
                                Notă: „Date" = mărimea reală a documentelor. „Storage" = spațiu alocat pe disc (include rezervă). Index-urile accelerează căutările.
                            </p>
                        </>
                    )}
                </div>
            )}

            {activeTab === "logs" && (
                <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md" data-testid="audit-logs-section">
                    <div className="divide-y divide-[#F1F3F5] dark:divide-[#1E232B]">
                        {logs.map(log => (
                            <div key={log.id} className="flex items-start gap-3 px-5 py-3 hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] transition-colors" data-testid={`log-row-${log.id}`}>
                                <div className="w-7 h-7 bg-gray-100 dark:bg-[#1E232B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs text-gray-500 font-bold">{log.actor?.name?.[0] || "?"}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-[#111827] dark:text-[#F3F4F6]">
                                        <span className="font-semibold">{log.actor?.name || "Sistem"}</span>
                                        {" "}{ACTION_LABELS[log.action] || log.action}
                                        {log.metadata?.title ? ` "${log.metadata.title}"` : ""}
                                        {log.metadata?.name ? ` "${log.metadata.name}"` : ""}
                                        {log.metadata?.from ? ` din "${log.metadata.from}" în "${log.metadata.to}"` : ""}
                                    </p>
                                    <p className="text-xs text-[#9CA3AF] mt-0.5">
                                        {new Date(log.created_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                                    </p>
                                </div>
                                <span className="text-xs text-[#9CA3AF] font-mono bg-[#F1F3F5] dark:bg-[#1E232B] px-1.5 py-0.5 rounded flex-shrink-0">{log.entity_type}</span>
                            </div>
                        ))}
                        {logs.length === 0 && <div className="text-center py-10 text-sm text-[#9CA3AF]">Niciun log disponibil</div>}
                    </div>
                </div>
            )}

            {/* Create User Modal */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreate(false)} />
                    <div className="relative bg-white dark:bg-[#151A21] rounded-md shadow-xl w-full max-w-md mx-4 z-10 border border-[#E4E4E7] dark:border-[#252B33]" data-testid="create-user-modal">
                        <div className="px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33]">
                            <h2 className="font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Utilizator nou</h2>
                        </div>
                        <form onSubmit={createUser} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Nume *</label>
                                <input data-testid="new-user-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Email *</label>
                                <input type="email" data-testid="new-user-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Parolă *</label>
                                <input type="password" data-testid="new-user-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Rol</label>
                                <select data-testid="new-user-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                                    className="w-full px-2 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                                    {ROLE_OPTIONS.filter(r => isSuper || r.value !== "super_admin").map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm text-[#4B5563] dark:text-[#9CA3AF] hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A]">Anulează</button>
                                <button data-testid="create-user-submit" type="submit" disabled={saving} className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se creează..." : "Creează"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setEditUser(null)} />
                    <div className="relative bg-white dark:bg-[#151A21] rounded-md shadow-xl w-full max-w-md mx-4 z-10 border border-[#E4E4E7] dark:border-[#252B33]" data-testid="edit-user-modal">
                        <div className="px-5 py-4 border-b border-[#E4E4E7] dark:border-[#252B33]">
                            <h2 className="font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                Editează {editUser.name}
                            </h2>
                            <p className="text-xs text-[#9CA3AF] mt-0.5">{editUser.email}</p>
                        </div>
                        <form onSubmit={saveEditUser} className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Nume</label>
                                <input data-testid="edit-user-name" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" required />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Poreclă</label>
                                <input data-testid="edit-user-nickname" value={editForm.nickname} onChange={e => setEditForm({ ...editForm, nickname: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Funcție</label>
                                <input data-testid="edit-user-job" value={editForm.job_title} onChange={e => setEditForm({ ...editForm, job_title: e.target.value })}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">Rol</label>
                                <select data-testid="edit-user-role" value={editForm.role}
                                    disabled={editUser.id === user?.id}
                                    onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                                    className="w-full px-2 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-60">
                                    {ROLE_OPTIONS.filter(r => isSuper || r.value !== "super_admin").map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                                {editUser.id === user?.id && <p className="text-[10px] text-[#9CA3AF] mt-1">Nu îți poți schimba propriul rol.</p>}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-[#4B5563] dark:text-[#9CA3AF] uppercase tracking-wide mb-1.5">
                                    Titlu custom <span className="text-[#9CA3AF] normal-case ml-1">(opțional)</span>
                                </label>
                                <input data-testid="edit-user-custom-role" value={editForm.custom_role_label} onChange={e => setEditForm({ ...editForm, custom_role_label: e.target.value })}
                                    placeholder="ex: Designer Senior, Copywriter..."
                                    maxLength={50}
                                    className="w-full px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                                <p className="text-[10px] text-[#9CA3AF] mt-1">
                                    Dacă e completat, va înlocui eticheta rolului în toată aplicația. Permisiunile rămân ale rolului de bază. Lasă gol pentru a reveni la eticheta standard.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setEditUser(null)} className="flex-1 py-2 border border-[#E4E4E7] dark:border-[#252B33] rounded-md text-sm text-[#4B5563] dark:text-[#9CA3AF] hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A]">Anulează</button>
                                <button data-testid="save-edit-user" type="submit" disabled={saving} className="flex-1 bg-[#002FA7] text-white py-2 rounded-md text-sm font-semibold hover:bg-[#002482] disabled:opacity-60">
                                    {saving ? "Se salvează..." : "Salvează"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
